import { millisecondsSince } from '../../../../language/time';
import { IAppState } from '../../IAppState';
import { CircuitBreaker } from './circuitBreaker';
import * as R from 'ramda';
import { ACTION_TYPE } from '../../actionType';


/**
 * Makes the default breaker: this is the breaker that we should use when there is no current breaker state for a
 * request type.
 */
export const makeDefaultBreaker = (action: ACTION_TYPE): CircuitBreaker => ({
  action,
  failureCount: 0,
  lastAttempt:  undefined,
});


/**
 * Updates the given breaker with failure information, returning a new breaker.
 */
export const commitFailure = (breaker: CircuitBreaker): CircuitBreaker => R.mergeDeepRight(
  breaker,
  {
    failureCount: breaker.failureCount + 1,
    lastAttempt:  new Date(),
  },
);


/**
 * Updates the given breaker with success information, returning a new breaker.
 */
export const commitSuccess = (breaker: CircuitBreaker): CircuitBreaker => R.mergeDeepRight(
  breaker,
  {
    failureCount: 0,
    lastAttempt:  new Date(),
  },
);


/**
 * Returns the existing circuit breaker for the given action from the given state if it exists. If the breaker doesn't
 * exist on the given state, returns a default breaker with 0 failures and undefined lastAttempt. Returns the default
 * model instead of undefined to make dependent operations idempotent by default.
 */
export const breakerFor = (
  state: IAppState,
  action: ACTION_TYPE,
): CircuitBreaker => state.breakerBox.breakers.get(action.toString()) || makeDefaultBreaker(action);


/**
 * More internal. Adds/updates a breaker in app state with failure information.
 */
export const addFailure = (state: IAppState, action: ACTION_TYPE): IAppState => ({
  ...state,
  breakerBox: {
    maxFailureCount: state.breakerBox.maxFailureCount,
    coolDownTimeMs:  state.breakerBox.coolDownTimeMs,
    breakers:        state.breakerBox.breakers.add(action, commitFailure(breakerFor(state, action))),
  },
});


/**
 * More internal. Adds/updates a breaker in app state with success information.
 */
export const addSuccess = (state: IAppState, action: ACTION_TYPE): IAppState => ({
  ...state,
  breakerBox: {
    maxFailureCount: state.breakerBox.maxFailureCount,
    coolDownTimeMs:  state.breakerBox.coolDownTimeMs,
    breakers:        state.breakerBox.breakers.add(action, commitSuccess(breakerFor(state, action))),
  },
});


/**
 * Forgets the failure history for the given action, so the next request is attempted rather than bailed out.
 * Breakers are keyed by action type alone, so use this when the thing being requested changes.
 */
export const clearBreaker = (state: IAppState, action: ACTION_TYPE): IAppState => ({
  ...state,
  breakerBox: {
    maxFailureCount: state.breakerBox.maxFailureCount,
    coolDownTimeMs:  state.breakerBox.coolDownTimeMs,
    breakers:        state.breakerBox.breakers.remove(action),
  },
});


/**
 * Easy function meant for reducers so they can easily update circuit breaker state with information from new request
 * data. Pure, returns new AppState.
 * @param state - App state
 * @param action - The API action to update
 * @param isError - Whether or not the request was successful.
 */
export const updateBreaker = (
  state: IAppState,
  action: ACTION_TYPE,
  isError: boolean,
): IAppState => isError ? addFailure(state, action) : addSuccess(state, action);


/**
 * Predicate for whether or not the given circuit breaker details means that a new request can be made
 * @param failureCount - The current number of failures
 * @param maxFailureCount - The maximum allowed failures per unit time
 * @param lastAttempt - The last time we tried a request and it failed
 * @param coolDownTimeMs - The number of milliseconds we should wait when the circuit has overloaded.
 */
export const isTripped = (
  failureCount: number,
  maxFailureCount: number,
  lastAttempt: Date | string | undefined,
  coolDownTimeMs: number,
): boolean => !(                                     // We're okay to make a request if...
  failureCount < maxFailureCount                     // we're under the failure max
  || !lastAttempt                                    // OR we haven't made an attempt yet
  || millisecondsSince(lastAttempt) > coolDownTimeMs // OR the cool down period has elapsed
);


/**
 * Returns whether or not the circuit breaker for the given action type has been tripped. If the breaker has been
 * tripped, it means that the request has failed too many times in too short an amount of time. `IAppState.breakerBox`
 * stores the values for how many failures are acceptable and what the cool-down time is.
 */
export const hasTrippedBreaker = (state: IAppState, action: ACTION_TYPE): boolean => {
  const breaker = breakerFor(state, action);
  return isTripped(
    breaker.failureCount,
    state.breakerBox.maxFailureCount,
    breaker.lastAttempt,
    state.breakerBox.coolDownTimeMs,
  );
};


/**
 * The last moment the breaker for the given action holds requests, once it has tripped: hasTrippedBreaker reads true
 * through it. Pure where hasTrippedBreaker reads the clock, so a selector can return it. In the past once the
 * cool-down ends, until a request succeeds.
 */
export const breakerHeldUntil = (state: IAppState, action: ACTION_TYPE): number | undefined => {
  const breaker = breakerFor(state, action);
  return breaker.failureCount >= state.breakerBox.maxFailureCount && breaker.lastAttempt
    ? new Date(breaker.lastAttempt).getTime() + state.breakerBox.coolDownTimeMs
    : undefined;
};


const MINUTE_MS = 60 * 1000;

/**
 * When to tell the user a tripped breaker releases. Rounded up to the minute so they never retry too early.
 * @param heldUntil - As breakerHeldUntil gives it
 */
export const shownReleaseTime = (heldUntil: number): Date =>
  new Date(Math.ceil((heldUntil + 1) / MINUTE_MS) * MINUTE_MS);

